import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { BookMarked, Milestone, Quote, KeyRound, Download, Loader2, Share2 } from "lucide-react";

export const LessonInfographic = ({ lesson, index }) => {
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const voci = lesson.voci_codex || [];
  const timeline = lesson.timeline || [];
  const hasContent = voci.length || timeline.length || lesson.citazione || lesson.password;
  if (!hasContent) return null;

  const slug = (lesson.argomento || "lectio").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const buildBlob = async () => {
    const bg = getComputedStyle(document.body).backgroundColor || "#f7f5f0";
    const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: bg });
    const res = await fetch(dataUrl);
    return { dataUrl, blob: await res.blob() };
  };

  const download = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const { dataUrl } = await buildBlob();
      const a = document.createElement("a");
      a.download = `scheda-${slug}.png`;
      a.href = dataUrl;
      a.click();
    } catch (e) {
      toast.error("Impossibile scaricare la scheda");
    } finally {
      setSaving(false);
    }
  };

  const share = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const { dataUrl, blob } = await buildBlob();
      const file = new File([blob], `scheda-${slug}.png`, { type: "image/png" });
      const shareData = {
        title: "Bottega di Filosofia",
        text: `Scheda della lectio "${lesson.argomento}" — Bottega di Filosofia`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
      } else {
        // fallback: scarica la scheda
        const a = document.createElement("a");
        a.download = `scheda-${slug}.png`;
        a.href = dataUrl;
        a.click();
        toast.info("Condivisione non supportata: la scheda è stata scaricata, puoi condividerla manualmente.");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Impossibile condividere la scheda");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="mt-12" data-testid="lesson-infographic-wrap">
      <div className="flex justify-end gap-2 mb-2">
        <button
          onClick={share}
          disabled={sharing || saving}
          data-testid="infographic-share-btn"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {sharing ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Share2 className="h-4 w-4" strokeWidth={1.5} />}
          Condividi
        </button>
        <button
          onClick={download}
          disabled={saving || sharing}
          data-testid="infographic-download-btn"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Download className="h-4 w-4" strokeWidth={1.5} />}
          Scarica la scheda
        </button>
      </div>
      <div
        ref={cardRef}
        className="rounded-xl border border-border overflow-hidden bg-card shadow-sm"
        data-testid="lesson-infographic"
      >
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-5 sm:px-7 py-5 flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold opacity-75">
            Scheda della Lectio{lesson.corso_nome ? ` · ${lesson.corso_nome}` : ""}
          </p>
          <h3 className="font-serif text-2xl font-bold leading-tight mt-0.5">{lesson.argomento}</h3>
        </div>
        <span className="ml-auto shrink-0 bg-accent text-accent-foreground font-bold text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
          Lectio {index + 1}
        </span>
      </div>

      <div className="p-5 sm:p-7">
        {/* Concetti chiave */}
        {voci.length > 0 && (
          <section className="mb-8">
            <SecLabel icon={BookMarked}>Concetti chiave</SecLabel>
            <div className="grid sm:grid-cols-2 gap-3">
              {voci.map((v) => (
                <div
                  key={v.termine}
                  data-testid="infographic-concept"
                  className="rounded-lg border border-border border-l-[3px] border-l-accent bg-secondary/40 px-4 py-3"
                >
                  <p className="font-serif text-lg font-bold italic text-primary">{v.termine}</p>
                  <p className="text-[13.5px] leading-snug text-muted-foreground mt-1">{v.definizione}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Il filo del pensiero */}
        {timeline.length > 0 && (
          <section className="mb-8">
            <SecLabel icon={Milestone}>Il filo del pensiero</SecLabel>
            <div className="relative pl-1.5" data-testid="infographic-timeline">
              <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
              {timeline.map((t, i) => (
                <div key={i} className={`relative pl-9 ${i === timeline.length - 1 ? "" : "pb-4"}`}>
                  <span className="absolute left-1 top-0.5 h-4 w-4 rounded-full bg-primary border-[3px] border-card ring-1 ring-border" />
                  <p className="text-xs font-bold text-accent-foreground/90" style={{ color: "hsl(43 60% 34%)" }}>
                    {t.tappa}
                  </p>
                  <p className="text-[14.5px] leading-snug mt-0.5">{t.testo}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Citazione + Password */}
        {(lesson.citazione || lesson.password) && (
          <div className="grid sm:grid-cols-[1.3fr_1fr] gap-4">
            {lesson.citazione && (
              <div className="rounded-lg bg-secondary/50 border-l-[3px] border-accent px-5 py-4">
                <SecLabel icon={Quote} className="mb-2.5">La citazione</SecLabel>
                <p className="font-serif italic text-lg leading-snug">"{lesson.citazione}"</p>
                <p className="text-[13px] text-muted-foreground font-semibold mt-2">— {lesson.citazione_autore}</p>
              </div>
            )}
            {lesson.password && (
              <div className="rounded-lg bg-primary text-primary-foreground px-5 py-4">
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold opacity-70 flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5" strokeWidth={2} /> Password
                </p>
                <p className="font-serif text-2xl font-extrabold mt-1 mb-1.5">{lesson.password}</p>
                {lesson.password_significato && (
                  <p className="text-[12.5px] leading-snug opacity-90">{lesson.password_significato}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Watermark (visibile anche nel PNG scaricato) */}
      <div className="border-t border-border bg-secondary/40 px-5 sm:px-7 py-3 flex items-center justify-center gap-2" data-testid="infographic-watermark">
        <span className="font-serif text-sm font-bold text-primary tracking-tight">Bottega di Filosofia</span>
        <span className="text-muted-foreground/60">·</span>
        <span className="text-[11px] text-muted-foreground">app.bottegadellafilosofia.it</span>
      </div>
    </div>
    </div>
  );
};

const SecLabel = ({ icon: Icon, children, className = "" }) => (
  <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-primary font-bold mb-3.5 ${className}`}>
    {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2} />}
    {children}
    <span className="flex-1 h-px bg-border" />
  </div>
);
